import React, { useEffect, useState, useCallback } from 'react';
// --- external imports ------

import firebase from '~/authentication/lib/firebase';
import { validateNewPassword, validateConfirmPassword, errorText } from '~/authentication/lib/credentialValidation';
import { ERROR_CODES, handleBaseError } from '~/authentication/lib/errors';
import { PASSWORD_MAX_LENGTH } from '~/authentication/lib/constants-auth';
import useAuthRequest from '~/authentication/lib/useAuthRequest';
import { LoadingCursor } from '~/containers/GameAsset/cursors';
// --- internal imports ------

import AuthBackdrop from '../AuthBackdrop';
import {
	ModalBackground,
	TitleText,
	FieldLabel,
	InputBackground,
	FieldInput,
	EyeButton,
	ErrorMessage,
	FORM_WIDTH_PROJECTION,
} from '~/authentication/AuthAssets';
import {
	StatusText,
	ResetButton,
	ResetButtonText,
	BackToLoginLink,
	INPUT_PROJECTION,
} from './ResetPasswordPageAssets';
// --- sibling imports ------

function ResetPasswordPage(props) {
	const [generalStatus, setGeneralStatus] = useState('Checking verification code...');
	const [passwordError, setPasswordError] = useState(null);
	const [confirmPasswordError, setConfirmPasswordError] = useState(null);
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
	const [passwordValue, setPasswordValue] = useState('');
	const [confirmPasswordValue, setConfirmPasswordValue] = useState('');

	const { isProcessing, runRequest } = useAuthRequest();

	useEffect(function verifyActionCode() {
		checkCode();
	}, [props.actionOobCode]);

	function handleFirebaseError(error) {
		switch (error?.code) {
		case ERROR_CODES.FIREBASE.RESET.EXPIRED_ACTION_CODE:
			setGeneralStatus('The action code has expired. Please try the forgot password form again.');
			break;
		case ERROR_CODES.FIREBASE.RESET.INVALID_ACTION_CODE:
			setGeneralStatus('The action code is invalid. Please try the forgot password form again.');
			break;
		case ERROR_CODES.FIREBASE.RESET.USER_DISABLED:
			setGeneralStatus('This user is disabled. Please contact HR.');
			break;
		case ERROR_CODES.FIREBASE.RESET.USER_NOT_FOUND:
			setGeneralStatus('This user was not found. Please contact HR.');
			break;
		default:
			setPasswordError(handleBaseError(error));
		}
	}

	async function checkCode() {
		if (!props.actionOobCode) {
			setGeneralStatus('No status code has been provided.');
			return;
		}

		try {
			await runRequest(() => firebase.verifyPasswordResetCode(props.actionOobCode));
			setGeneralStatus(null);
		}
		catch (error) {
			handleFirebaseError(error);
		}
	}

	const handleOnSubmit = useCallback(async () => {
		const { isValid: isValidPassword, error: pwError } = validateNewPassword(passwordValue);
		setPasswordError(pwError);
		if (!isValidPassword) {
			return;
		}

		const { isValid: isValidConfirm, error: cpError } = validateConfirmPassword(passwordValue, confirmPasswordValue);
		setConfirmPasswordError(cpError);
		if (!isValidConfirm) {
			return;
		}

		try {
			await runRequest(() => firebase.confirmPasswordReset(props.actionOobCode, passwordValue));
			setGeneralStatus('Password reset successful. You may now log in.');
		}
		catch (error) {
			handleFirebaseError(error);
		}
	}, [passwordValue, confirmPasswordValue, props.actionOobCode, runRequest]);

	const handleOnPasswordBlur = useCallback((e) => {
		const { error: pwError } = validateNewPassword(e.target.value);
		setPasswordError(pwError);
		const { error: cpError } = validateConfirmPassword(e.target.value, confirmPasswordValue);
		setConfirmPasswordError(cpError);
	}, [confirmPasswordValue]);

	const handleOnConfirmPasswordBlur = useCallback((e) => {
		const { error: cpError } = validateConfirmPassword(passwordValue, e.target.value);
		setConfirmPasswordError(cpError);
	}, [passwordValue]);

	const handlePasswordChange = useCallback((e) => {
		setPasswordValue(e.target.value);
	}, []);

	const handleConfirmPasswordChange = useCallback((e) => {
		setConfirmPasswordValue(e.target.value);
	}, []);

	const handleTogglePasswordVisible = useCallback(() => {
		setIsPasswordVisible((prev) => !prev);
	}, []);

	const handleToggleConfirmPasswordVisible = useCallback(() => {
		setIsConfirmPasswordVisible((prev) => !prev);
	}, []);

	const shouldUsePasswordType = !isPasswordVisible || passwordValue.length === 0;
	const shouldUseConfirmPasswordType = !isConfirmPasswordVisible || confirmPasswordValue.length === 0;

	return (
		<AuthBackdrop>
			{isProcessing && <LoadingCursor />}

			<ModalBackground
				x={528}
				y={148}
				shouldFadeIn={false}
			/>

			<TitleText
				x={644}
				y={212}
				projection={FORM_WIDTH_PROJECTION}
			>
				RESET PASSWORD
			</TitleText>

			{generalStatus !== null ? (
				<StatusText
					data-testid="reset-status"
					x={644}
					y={468}
					projection={FORM_WIDTH_PROJECTION}
					shouldFadeIn={false}
				>
					{generalStatus}
				</StatusText>
			) : (
				<>
					{/* Password */}
					<FieldLabel
						x={644}
						y={468}
						projection={FORM_WIDTH_PROJECTION}
					>
						New Password
					</FieldLabel>

					<InputBackground
						x={644}
						y={520}
						projection={INPUT_PROJECTION}
					/>

					<FieldInput
						data-testid="reset-password"
						id="password"
						x={644}
						y={520}
						projection={INPUT_PROJECTION}
						type={shouldUsePasswordType ? 'password' : 'text'}
						placeholder="New Password"
						maxLength={PASSWORD_MAX_LENGTH}
						value={passwordValue}
						onChange={handlePasswordChange}
						onBlur={handleOnPasswordBlur}
						disabled={isProcessing}
						hasError={!!passwordError}
					/>

					<EyeButton
						data-testid="reset-eye"
						x={1226}
						y={548}
						isActive={isPasswordVisible}
						onClick={handleTogglePasswordVisible}
						disabled={isProcessing}
						shouldFadeIn={false}
					/>

					{passwordError && (
						<ErrorMessage
							x={644}
							y={604}
							projection={FORM_WIDTH_PROJECTION}
						>
							{errorText(passwordError)}
						</ErrorMessage>
					)}

					{/* Confirm Password */}
					<FieldLabel
						x={644}
						y={620}
						projection={FORM_WIDTH_PROJECTION}
					>
						Confirm Password
					</FieldLabel>

					<InputBackground
						x={644}
						y={676}
						projection={INPUT_PROJECTION}
					/>

					<FieldInput
						data-testid="reset-password-confirm"
						id="confirmPassword"
						x={644}
						y={676}
						projection={INPUT_PROJECTION}
						type={shouldUseConfirmPasswordType ? 'password' : 'text'}
						placeholder="Confirm Password"
						maxLength={PASSWORD_MAX_LENGTH}
						value={confirmPasswordValue}
						onChange={handleConfirmPasswordChange}
						onBlur={handleOnConfirmPasswordBlur}
						disabled={isProcessing}
						hasError={!!confirmPasswordError}
					/>

					<EyeButton
						data-testid="reset-eye-confirm"
						x={1226}
						y={704}
						isActive={isConfirmPasswordVisible}
						onClick={handleToggleConfirmPasswordVisible}
						disabled={isProcessing}
						shouldFadeIn={false}
					/>

					{confirmPasswordError && (
						<ErrorMessage
							x={644}
							y={760}
							projection={FORM_WIDTH_PROJECTION}
						>
							{errorText(confirmPasswordError)}
						</ErrorMessage>
					)}

					<ResetButton
						data-testid="reset-submit"
						x={865}
						y={896}
						onClick={handleOnSubmit}
						disabled={isProcessing}
						shouldFadeIn={false}
					/>

					<ResetButtonText
						data-testid="reset-submit-text"
						x={926}
						y={907}
						shouldFadeIn={false}
					>
						Reset
					</ResetButtonText>
				</>
			)}

			<BackToLoginLink
				x={644}
				y={800}
				projection={FORM_WIDTH_PROJECTION}
				to="/login"
				shouldFadeIn={false}
			>
				Back to Login
			</BackToLoginLink>
		</AuthBackdrop>
	);
}

export default ResetPasswordPage;
